<template>
    <div
        class="adapters_data_table_root"
        :class="{
            'adapters_data_table__frozen_head': frozenHead,
            'is-frozen-head-active': isFrozenHeadActive,
        }"
    >
        <DataTable
            v-bind="getDataTableBindings"
            @page="onPage($event)"
            @sort="onSort($event)"
            :pt="pt"
            class="adapters_data_table"
        >
            <slot name="header"></slot>
            <template #empty>
                <slot name="empty">
                    <div class="block w-full text-center" v-bind="getPT('emptyMessage')">
                        <span>{{ $t('lang.main.table.no_data_template') }}</span>
                    </div>
                </slot>
            </template>
            <slot></slot>
            <template v-if="useCustomPagination || $slots.footer" #footer>
                <div class="flex flex-column">
                    <div v-if="useCustomPagination" class="flex flex-column sm:flex-row justify-content-between gap-2">
                        <Paginator
                            :rows="getMergedParams.rows"
                            :totalRecords="getMergedParams.totalRecords"
                            :rowsPerPageOptions="getMergedParams.rowsPerPageOptions"
                            :template="getMergedParams.paginatorLeftTemplate"
                            @page="onPage($event)"
                            :first="getMergedParams.first"
                        />
                        <Paginator
                            :rows="getMergedParams.rows"
                            :totalRecords="getMergedParams.totalRecords"
                            :rowsPerPageOptions="getMergedParams.rowsPerPageOptions"
                            :currentPageReportTemplate="$t('lang.main.table.current_info_template', ['{first}', '{last}', '{totalRecords}'])"
                            :template="getMergedParams.paginatorRightTemplate"
                            :first="getMergedParams.first"
                            @page="onPage($event)"
                        />
                    </div>
                    <div v-if="$slots.footer">
                        <slot name="footer"></slot>
                    </div>
                </div>
            </template>
        </DataTable>
        <!-- Фиксированный горизонтальный скролл у низа экрана, пока нативный вне вьюпорта -->
        <Teleport to="body">
            <div
                v-if="fixedHorizontalScroll"
                ref="fixedHScroll"
                class="adapters_data_table__fixed_h_scroll"
                style="display: none;"
                @scroll.passive="onFixedHScroll"
            >
                <div ref="fixedHScrollContent" class="adapters_data_table__fixed_h_scroll_content"></div>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { nextTick } from "vue";
import DataTable from "primevue/datatable";
import {TableHelper} from "../../helpers/TableHelper";
import uniqueId from "lodash/uniqueId";
import Paginator from "primevue/paginator";
import PassThrough from "keysso/mixins/PassThrough";
import BrowserStorage from 'keysso/mixins/BrowserStorage';
import {StorageKeys} from 'keysso/constants/storageKeys';

/**
 * @param {Object} params параметры пакета
 * @param {Boolean} useCustomPagination использование пагинатора по дизайну
 * @param {Boolean} frozenHead фиксация шапки при вертикальном скролле
 * @param {Number} frozenHeadPaddingTop доп. отступ сверху закреплённой шапки (для выноса легенды)
 * @param {Boolean} fixedHorizontalScroll фиксированный горизонтальный скролл у низа экрана
 */
export default {
    // Корень — обёртка: attrs (v-model:selection и т.п.) пробрасываем во внутренний DataTable
    inheritAttrs: false,
    emits: ['page', 'sort'],
    mixins: [PassThrough, BrowserStorage],
    name: "AdaptersDataTable",
    props: {
        params: {
            type: Object,
            required: true,
        },
        useCustomPagination: {
            type: Boolean,
            required: false,
            default: true,
        },
        pt: {
            type: Object,
            required: false,
        },
        // фиксация заголовка таблицы при прокрутке
        frozenHead: {
            type: Boolean,
            required: false,
            default: true,
        },
        // доп. padding сверху только у таблиц с выносом заголовка за ячейку
        frozenHeadPaddingTop: {
            type: Number,
            required: false,
            default: 0,
        },
        // фиксированный горизонтальный скролл у низа экрана, пока таблица не вмещается целиком
        fixedHorizontalScroll: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            defaultParams: {
                paginatorPosition: "bottom",
                paginator: false,
                rowsPerPageOptions: this.getRowsPerPageOptions(),
                paginatorLeftTemplate: this.getPaginatorLeftTemplate(),
                paginatorRightTemplate: this.getPaginatorRightTemplate(),
                lazy: true,
                ref: 'data_table_component_' + uniqueId(),
                scrollable: true,
                first: 0,
            },
            frozenHeadRafId: null,
            isFrozenHeadActive: false,
            frozenHeadHeight: 0,
            frozenHeadActivationThreshold: 10,
            tableWrapperEl: null,
            // Кэш DOM при активной фиксации шапки — быстрый горизонтальный скролл
            _frozenHeadTableEl: null,
            _frozenHeadTheadEl: null,
            _frozenHeadThEls: null,
            /** Счётчик смены данных таблицы — для пропуска тяжёлого sync шапки при клике по чекбоксам */
            tableDataRevision: 0,
            frozenHeadSyncedRevision: -1,
            // Фиксированный горизонтальный скролл
            fixedHScrollRafId: null,
            isFixedHScrollVisible: false,
            _isSyncingHScroll: false,
            _fixedHScrollResizeObserver: null,
        }
    },
    computed: {
        getMergedParams() {
            return {...this.defaultParams, ...this.params}
        },
        /** $attrs (selection и др.) + params; params важнее при пересечении ключей */
        getDataTableBindings() {
            return {...this.$attrs, ...this.getMergedParams};
        },
        needsWrapperScrollListener() {
            return this.frozenHead || this.fixedHorizontalScroll;
        },
    },
    watch: {
        'getMergedParams.value': {
            handler(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.tableDataRevision += 1;
                }
            },
        },
    },
    mounted() {
        if (this.frozenHead) {
            window.addEventListener('scroll', this.scheduleFrozenHeadSync, { passive: true });
            window.addEventListener('resize', this.scheduleFrozenHeadSync, { passive: true });
        }
        if (this.fixedHorizontalScroll) {
            // Visibility — синхронно на scroll, иначе при быстром скролле полоска «догоняет»
            window.addEventListener('scroll', this.syncFixedHScrollVisibility, { passive: true });
            window.addEventListener('resize', this.scheduleFixedHScrollSync, { passive: true });
        }
        if (this.needsWrapperScrollListener) {
            this.bindWrapperScrollListener();
        }
        nextTick(() => {
            if (this.frozenHead) {
                this.scheduleFrozenHeadSync();
            }
            if (this.fixedHorizontalScroll) {
                this.scheduleFixedHScrollSync();
            }
        });
    },
    updated() {
        if (!this.frozenHead) {
            // Данные/разметка могли смениться без frozen head — подстрахуем fixed scroll
            if (this.fixedHorizontalScroll && this.tableDataRevision !== this.frozenHeadSyncedRevision) {
                this.frozenHeadSyncedRevision = this.tableDataRevision;
                this.bindWrapperScrollListener();
                nextTick(this.scheduleFixedHScrollSync);
            }
            return;
        }
        // Перерисовка без смены value (чекбоксы и т.п.) — не трогаем DOM фиксированной шапки
        if (this.isFrozenHeadActive && this.tableDataRevision === this.frozenHeadSyncedRevision) {
            return;
        }
        this.frozenHeadSyncedRevision = this.tableDataRevision;
        this.bindWrapperScrollListener();
        nextTick(() => {
            this.scheduleFrozenHeadSync();
            if (this.fixedHorizontalScroll) {
                this.scheduleFixedHScrollSync();
            }
        });
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.scheduleFrozenHeadSync);
        window.removeEventListener('resize', this.scheduleFrozenHeadSync);
        window.removeEventListener('scroll', this.syncFixedHScrollVisibility);
        window.removeEventListener('resize', this.scheduleFixedHScrollSync);
        this.unbindWrapperScrollListener();
        this.unbindFixedHScrollResizeObserver();
        this.resetFrozenHeadStyles();

        if (this.frozenHeadRafId) {
            cancelAnimationFrame(this.frozenHeadRafId);
            this.frozenHeadRafId = null;
        }
        if (this.fixedHScrollRafId) {
            cancelAnimationFrame(this.fixedHScrollRafId);
            this.fixedHScrollRafId = null;
        }
    },
    methods: {
        bindWrapperScrollListener() {
            const nextWrapper = this.getTableWrapperEl();
            if (this.tableWrapperEl === nextWrapper) {
                this.bindFixedHScrollResizeObserver(nextWrapper);
                return;
            }

            this.unbindWrapperScrollListener();
            this.tableWrapperEl = nextWrapper;
            this.tableWrapperEl?.addEventListener('scroll', this.onTableWrapperScroll, { passive: true });
            this.bindFixedHScrollResizeObserver(nextWrapper);
        },
        unbindWrapperScrollListener() {
            this.tableWrapperEl?.removeEventListener('scroll', this.onTableWrapperScroll);
            this.tableWrapperEl = null;
            this.unbindFixedHScrollResizeObserver();
        },
        bindFixedHScrollResizeObserver(wrapperEl) {
            if (!this.fixedHorizontalScroll) {
                return;
            }
            if (!wrapperEl) {
                this.unbindFixedHScrollResizeObserver();
                return;
            }
            if (!this._fixedHScrollResizeObserver) {
                this._fixedHScrollResizeObserver = new ResizeObserver(() => {
                    this.scheduleFixedHScrollSync();
                });
            }
            this.unbindFixedHScrollResizeObserver(false);
            this._fixedHScrollResizeObserver.observe(wrapperEl);
            const tableEl = wrapperEl.querySelector('.p-datatable-table');
            if (tableEl) {
                this._fixedHScrollResizeObserver.observe(tableEl);
            }
        },
        unbindFixedHScrollResizeObserver(disconnect = true) {
            if (!this._fixedHScrollResizeObserver) {
                return;
            }
            this._fixedHScrollResizeObserver.disconnect();
            if (disconnect) {
                this._fixedHScrollResizeObserver = null;
            }
        },
        onPage(event) {
            this.$emit('page', {...this.$refs[this.getMergedParams.ref], ...event});
            this.setLocalStorageItem(StorageKeys.GLOBAL_TABLE_PER_PAGE, event.rows);
            this.resyncFrozenHead();
            this.resyncFixedHScroll();
        },
        onSort(event) {
            this.$emit('sort', {...this.$refs[this.getMergedParams.ref], ...event});
            this.resyncFrozenHead();
            this.resyncFixedHScroll();
        },
        resyncFrozenHead() {
            if (!this.frozenHead) {
                return;
            }

            this.bindWrapperScrollListener();
            nextTick(() => {
                this.scheduleFrozenHeadSync();
                requestAnimationFrame(() => this.scheduleFrozenHeadSync());
            });
        },
        /**
         * Снять зафиксированные px-ширины, чтобы CSS (--cell-w) пересчитал колонки.
         * Нужно при смене вида, пока шапка уже frozen.
         */
        relayoutFrozenColumns() {
            const tableEl = this.$el?.querySelector('.p-datatable-table');
            const theadEl = tableEl?.querySelector('.p-datatable-thead');
            if (!tableEl || !theadEl) {
                return;
            }

            tableEl.querySelectorAll(':scope > colgroup > col').forEach((colEl) => {
                colEl.style.width = '';
                colEl.style.minWidth = '';
                colEl.style.maxWidth = '';
            });
            tableEl.querySelectorAll(':scope > tbody > tr:first-child > td').forEach((tdEl) => {
                tdEl.style.width = '';
                tdEl.style.minWidth = '';
                tdEl.style.maxWidth = '';
            });
            theadEl.querySelectorAll(':scope > tr > th').forEach((thEl) => {
                thEl.style.width = '';
                thEl.style.minWidth = '';
                thEl.style.maxWidth = '';
            });

            requestAnimationFrame(() => {
                if (this.isFrozenHeadActive) {
                    const nextTableEl = this.$el?.querySelector('.p-datatable-table');
                    const nextTheadEl = nextTableEl?.querySelector('.p-datatable-thead');
                    if (nextTableEl && nextTheadEl) {
                        const leafSlotWidths = this.syncFrozenHeadCellWidths(nextTheadEl, nextTableEl);
                        this.syncTableLeafWidths(nextTableEl, leafSlotWidths);
                    }
                }
                this.scheduleFrozenHeadSync();
                this.scheduleFixedHScrollSync();
            });
        },
        resyncFixedHScroll() {
            if (!this.fixedHorizontalScroll) {
                return;
            }

            this.bindWrapperScrollListener();
            nextTick(() => {
                this.scheduleFixedHScrollSync();
                requestAnimationFrame(() => this.scheduleFixedHScrollSync());
            });
        },
        getRowsPerPageOptions() {
            return TableHelper.getRowsPerPageOptions();
        },
        getPaginatorLeftTemplate() {
            return TableHelper.getPaginatorLeftTemplate();
        },
        getPaginatorRightTemplate() {
            return TableHelper.getPaginatorRightTemplate();
        },
        getTopMenuHeight() {
            const menuEl = document.querySelector('.menu_top__container');
            return menuEl?.getBoundingClientRect().height || 0;
        },
        getTableWrapperEl() {
            return this.$el?.querySelector('.p-datatable-wrapper') || null;
        },
        /** Горизонтальный скролл: синхронно, без RAF — шапка не отстаёт при лагах */
        onTableWrapperScroll() {
            if (this.frozenHead && this.isFrozenHeadActive) {
                this.applyFrozenHeadHorizontalOffset();
            }
            if (this.fixedHorizontalScroll) {
                this.syncFixedHScrollFromWrapper();
            }
        },
        scheduleFixedHScrollSync() {
            if (!this.fixedHorizontalScroll || this.fixedHScrollRafId) {
                return;
            }

            this.fixedHScrollRafId = requestAnimationFrame(() => {
                this.fixedHScrollRafId = null;
                this.syncFixedHScrollVisibility();
            });
        },
        syncFixedHScrollFromWrapper() {
            if (this._isSyncingHScroll || !this.isFixedHScrollVisible) {
                return;
            }
            const wrapperEl = this.tableWrapperEl || this.getTableWrapperEl();
            const fixedEl = this.$refs.fixedHScroll;
            if (!wrapperEl || !fixedEl) {
                return;
            }
            if (fixedEl.scrollLeft === wrapperEl.scrollLeft) {
                return;
            }
            this._isSyncingHScroll = true;
            fixedEl.scrollLeft = wrapperEl.scrollLeft;
            this._isSyncingHScroll = false;
        },
        onFixedHScroll() {
            if (this._isSyncingHScroll || !this.isFixedHScrollVisible) {
                return;
            }
            const wrapperEl = this.tableWrapperEl || this.getTableWrapperEl();
            const fixedEl = this.$refs.fixedHScroll;
            if (!wrapperEl || !fixedEl) {
                return;
            }
            if (wrapperEl.scrollLeft === fixedEl.scrollLeft) {
                return;
            }
            this._isSyncingHScroll = true;
            wrapperEl.scrollLeft = fixedEl.scrollLeft;
            if (this.frozenHead && this.isFrozenHeadActive) {
                this.applyFrozenHeadHorizontalOffset();
            }
            this._isSyncingHScroll = false;
        },
        /** Мгновенно через DOM — без Vue-рендера, чтобы подмена со нативным была незаметна */
        applyFixedHScrollDomState(shouldShow, wrapperEl, wrapperRect) {
            const fixedEl = this.$refs.fixedHScroll;
            if (!fixedEl) {
                return;
            }

            if (!shouldShow) {
                if (fixedEl.style.display !== 'none') {
                    fixedEl.style.display = 'none';
                }
                this.isFixedHScrollVisible = false;
                return;
            }

            const left = `${Math.round(wrapperRect.left)}px`;
            const width = `${Math.round(wrapperRect.width)}px`;
            const innerWidth = `${wrapperEl.scrollWidth}px`;

            if (fixedEl.style.display !== 'block') {
                fixedEl.style.display = 'block';
            }
            if (fixedEl.style.left !== left) {
                fixedEl.style.left = left;
            }
            if (fixedEl.style.width !== width) {
                fixedEl.style.width = width;
            }

            const contentEl = this.$refs.fixedHScrollContent;
            if (contentEl && contentEl.style.width !== innerWidth) {
                contentEl.style.width = innerWidth;
            }

            if (fixedEl.scrollLeft !== wrapperEl.scrollLeft) {
                this._isSyncingHScroll = true;
                fixedEl.scrollLeft = wrapperEl.scrollLeft;
                this._isSyncingHScroll = false;
            }

            this.isFixedHScrollVisible = true;
        },
        syncFixedHScrollVisibility() {
            if (!this.fixedHorizontalScroll) {
                return;
            }

            const wrapperEl = this.tableWrapperEl || this.getTableWrapperEl();
            if (!wrapperEl) {
                this.applyFixedHScrollDomState(false);
                return;
            }

            const hasHOverflow = wrapperEl.scrollWidth > wrapperEl.clientWidth + 1;
            const wrapperRect = wrapperEl.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            // Таблица хотя бы частично видна во вьюпорте
            const intersectsViewport = wrapperRect.top < viewportHeight && wrapperRect.bottom > 0;
            // Нативный скролл ещё ниже края экрана — показываем фиксированный
            const nativeScrollbarOutOfView = wrapperRect.bottom > viewportHeight;

            const shouldShow = hasHOverflow && intersectsViewport && nativeScrollbarOutOfView;
            this.applyFixedHScrollDomState(shouldShow, wrapperEl, wrapperRect);
        },
        applyFrozenHeadHorizontalOffset() {
            const wrapperEl = this.tableWrapperEl || this.getTableWrapperEl();
            const tableEl = this._frozenHeadTableEl;
            const theadEl = this._frozenHeadTheadEl;
            if (!wrapperEl || !tableEl || !theadEl || !this.isFrozenHeadActive) {
                return;
            }

            const scrollLeft = Math.round(wrapperEl.scrollLeft);
            const wrapperRect = wrapperEl.getBoundingClientRect();
            const tableWidth = tableEl.scrollWidth;
            const wrapperVisibleWidth = wrapperEl.clientWidth;
            const leftClip = Math.max(0, scrollLeft);
            const rightClip = Math.max(0, tableWidth - (scrollLeft + wrapperVisibleWidth));

            theadEl.style.left = `${wrapperRect.left}px`;
            theadEl.style.width = `${tableWidth}px`;
            theadEl.style.overflow = '';
            theadEl.style.clipPath = `inset(0 ${rightClip}px 0 ${leftClip}px)`;
            theadEl.style.transform = `translate3d(${-scrollLeft}px, 0, 0)`;

            const frozenThEls = this._frozenHeadThEls;
            if (!frozenThEls) {
                return;
            }
            for (let i = 0; i < frozenThEls.length; i++) {
                frozenThEls[i].style.transform = `translate3d(${scrollLeft}px, 0, 0)`;
            }
        },
        cacheFrozenHeadElements(tableEl, theadEl) {
            this._frozenHeadTableEl = tableEl;
            this._frozenHeadTheadEl = theadEl;
            this._frozenHeadThEls = Array.from(theadEl.querySelectorAll('th.p-frozen-column'));
        },
        clearFrozenHeadElementCache() {
            this._frozenHeadTableEl = null;
            this._frozenHeadTheadEl = null;
            this._frozenHeadThEls = null;
        },
        scheduleFrozenHeadSync() {
            if (!this.frozenHead || this.frozenHeadRafId) {
                return;
            }

            this.frozenHeadRafId = requestAnimationFrame(() => {
                this.frozenHeadRafId = null;
                this.syncFrozenHeadPosition();
            });
        },
        resetFrozenHeadStyles() {
            const rootEl = this.$el;
            const tableEl = rootEl?.querySelector('.p-datatable-table');
            const theadEl = tableEl?.querySelector('.p-datatable-thead');
            const wrapperEl = this.getTableWrapperEl();
            if (!tableEl || !theadEl) {
                return;
            }

            theadEl.style.position = '';
            theadEl.style.top = '';
            theadEl.style.left = '';
            theadEl.style.width = '';
            theadEl.style.transform = '';
            theadEl.style.clipPath = '';
            theadEl.style.overflow = '';
            theadEl.style.paddingTop = '';
            theadEl.style.zIndex = '';
            theadEl.style.pointerEvents = '';
            theadEl.style.transition = '';
            tableEl.style.marginTop = '';
            tableEl.style.transition = '';
            if (wrapperEl) {
                wrapperEl.style.paddingTop = '';
                wrapperEl.style.transition = '';
            }
            tableEl.querySelectorAll(':scope > colgroup > col').forEach((colEl) => {
                colEl.style.width = '';
                colEl.style.minWidth = '';
                colEl.style.maxWidth = '';
            });
            tableEl.querySelectorAll(':scope > tbody > tr:first-child > td').forEach((tdEl) => {
                tdEl.style.width = '';
                tdEl.style.minWidth = '';
                tdEl.style.maxWidth = '';
            });
            theadEl.querySelectorAll(':scope > tr > th').forEach((thEl) => {
                thEl.style.width = '';
                thEl.style.minWidth = '';
                thEl.style.maxWidth = '';
                thEl.style.transform = '';
            });
            this.isFrozenHeadActive = false;
            this.clearFrozenHeadElementCache();
        },
        syncTableLeafWidths(tableEl, leafSlotWidths) {
            if (!leafSlotWidths.length) {
                return;
            }

            const colEls = Array.from(tableEl.querySelectorAll(':scope > colgroup > col'));
            if (colEls.length >= leafSlotWidths.length) {
                leafSlotWidths.forEach((width, index) => {
                    if (!width || !colEls[index]) {
                        return;
                    }
                    colEls[index].style.width = `${width}px`;
                    colEls[index].style.minWidth = `${width}px`;
                    colEls[index].style.maxWidth = `${width}px`;
                });
                return;
            }

            const firstRowCells = Array.from(tableEl.querySelectorAll(':scope > tbody > tr:first-child > td'));
            firstRowCells.forEach((tdEl, index) => {
                const width = leafSlotWidths[index];
                if (!width) {
                    return;
                }
                tdEl.style.width = `${width}px`;
                tdEl.style.minWidth = `${width}px`;
                tdEl.style.maxWidth = `${width}px`;
            });
        },
        getTableLeafWidths(tableEl, totalLeafSlots) {
            if (!tableEl || !totalLeafSlots) {
                return [];
            }

            const leafSlotWidths = Array(totalLeafSlots).fill(0);
            const colEls = Array.from(tableEl.querySelectorAll(':scope > colgroup > col'));
            if (colEls.length >= totalLeafSlots) {
                colEls.slice(0, totalLeafSlots).forEach((colEl, index) => {
                    const width = colEl.getBoundingClientRect().width || colEl.offsetWidth || 0;
                    if (width > 0) {
                        leafSlotWidths[index] = width;
                    }
                });

                if (leafSlotWidths.some((width) => width > 0)) {
                    return leafSlotWidths;
                }
            }

            const firstRowCellEls = Array.from(tableEl.querySelectorAll(':scope > tbody > tr:first-child > td'));
            if (!firstRowCellEls.length) {
                return leafSlotWidths;
            }

            let slotIndex = 0;
            firstRowCellEls.forEach((cellEl) => {
                if (slotIndex >= totalLeafSlots) {
                    return;
                }
                const colSpan = Number(cellEl.getAttribute('colspan') || 1);
                const span = Math.max(1, colSpan);
                const cellWidth = cellEl.getBoundingClientRect().width || cellEl.offsetWidth || 0;
                const slotWidth = span > 1 ? cellWidth / span : cellWidth;
                for (let i = 0; i < span && slotIndex + i < totalLeafSlots; i += 1) {
                    if (slotWidth > 0) {
                        leafSlotWidths[slotIndex + i] = slotWidth;
                    }
                }
                slotIndex += span;
            });

            return leafSlotWidths;
        },
        syncFrozenHeadCellWidths(theadEl, tableEl) {
            const rowEls = Array.from(theadEl.querySelectorAll(':scope > tr'));
            if (!rowEls.length) {
                return [];
            }

            const cellRows = rowEls.map((rowEl) => Array.from(rowEl.querySelectorAll(':scope > th')));
            // Build a header grid map with proper rowspan/colspan handling.
            const occupancy = [];
            const mappedCells = [];
            let totalLeafSlots = 0;
            const rowCount = cellRows.length;

            cellRows.forEach((cells, rowIndex) => {
                if (!occupancy[rowIndex]) {
                    occupancy[rowIndex] = [];
                }

                let colIndex = 0;
                cells.forEach((cellEl) => {
                    while (occupancy[rowIndex][colIndex]) {
                        colIndex += 1;
                    }

                    const colSpan = Number(cellEl.getAttribute('colspan') || 1);
                    const rowSpan = Number(cellEl.getAttribute('rowspan') || 1);
                    const startIndex = colIndex;
                    const endIndex = startIndex + colSpan;
                    mappedCells.push({ cellEl, startIndex, endIndex, rowIndex, rowSpan });

                    for (let r = rowIndex; r < rowIndex + rowSpan; r += 1) {
                        if (!occupancy[r]) {
                            occupancy[r] = [];
                        }
                        for (let c = startIndex; c < endIndex; c += 1) {
                            occupancy[r][c] = true;
                        }
                    }

                    colIndex = endIndex;
                    totalLeafSlots = Math.max(totalLeafSlots, endIndex);
                });
            });

            if (!totalLeafSlots) {
                return [];
            }

            // Prefer real table leaf widths (colgroup/body), especially important for unequal colspan groups.
            const leafSlotWidths = this.getTableLeafWidths(tableEl, totalLeafSlots);
            mappedCells.forEach(({ cellEl, startIndex, endIndex, rowIndex, rowSpan }) => {
                const endsAtLastRow = rowIndex + rowSpan === rowCount;
                if (!endsAtLastRow) {
                    return;
                }

                const span = endIndex - startIndex;
                const hasMeasuredWidths = leafSlotWidths
                    .slice(startIndex, endIndex)
                    .some((width) => width > 0);
                if (hasMeasuredWidths) {
                    return;
                }

                const cellWidth = cellEl.getBoundingClientRect().width;
                const slotWidth = span > 1 ? cellWidth / span : cellWidth;
                for (let i = startIndex; i < endIndex; i += 1) {
                    leafSlotWidths[i] = slotWidth;
                }
            });

            mappedCells.forEach(({ cellEl, startIndex, endIndex }) => {
                const width = leafSlotWidths
                    .slice(startIndex, endIndex)
                    .reduce((sum, item) => sum + item, 0);

                if (!width) {
                    return;
                }

                cellEl.style.width = `${width}px`;
                cellEl.style.minWidth = `${width}px`;
                cellEl.style.maxWidth = `${width}px`;
            });

            return leafSlotWidths;
        },
        syncFrozenHeadPosition() {
            const rootEl = this.$el;
            const tableEl = rootEl?.querySelector('.p-datatable-table');
            const theadEl = tableEl?.querySelector('.p-datatable-thead');
            const wrapperEl = this.getTableWrapperEl();
            if (!tableEl || !theadEl || !wrapperEl) {
                return;
            }

            const menuHeight = this.getTopMenuHeight();
            const wrapperRect = wrapperEl.getBoundingClientRect();
            const theadRect = theadEl.getBoundingClientRect();
            const initialHeadHeight = theadEl.offsetHeight || theadEl.getBoundingClientRect().height;
            const activationTop = menuHeight - this.frozenHeadActivationThreshold;
            const deactivationTop = menuHeight + this.frozenHeadActivationThreshold;
            const canFreezeByBottom = wrapperRect.bottom > menuHeight + initialHeadHeight;
            const shouldFreeze = this.isFrozenHeadActive
                ? wrapperRect.top <= deactivationTop && canFreezeByBottom
                : theadRect.top <= activationTop && canFreezeByBottom;

            if (!shouldFreeze) {
                if (this.isFrozenHeadActive) {
                    this.resetFrozenHeadStyles();
                }
                this.clearFrozenHeadElementCache();
                return;
            }

            if (!this.isFrozenHeadActive) {
                const leafSlotWidths = this.syncFrozenHeadCellWidths(theadEl, tableEl);
                this.syncTableLeafWidths(tableEl, leafSlotWidths);
            }
            const isEnteringFrozenMode = !this.isFrozenHeadActive;
            this.isFrozenHeadActive = true;

            // Prevent one-frame jump: reserve header space before removing thead from normal flow.
            wrapperEl.style.transition = '';
            wrapperEl.style.paddingTop = `${Math.ceil(initialHeadHeight)}px`;

            const scrollLeft = Math.round(wrapperEl.scrollLeft);
            const leftOffset = wrapperRect.left;
            const tableWidth = tableEl.scrollWidth;
            const wrapperVisibleWidth = wrapperEl.clientWidth;
            const leftClip = Math.max(0, scrollLeft);
            const rightClip = Math.max(0, tableWidth - (scrollLeft + wrapperVisibleWidth));
            this.cacheFrozenHeadElements(tableEl, theadEl);
            theadEl.style.position = 'fixed';
            theadEl.style.top = `${menuHeight}px`;
            theadEl.style.left = `${leftOffset}px`;
            theadEl.style.width = `${tableWidth}px`;
            theadEl.style.overflow = '';
            theadEl.style.paddingTop = this.frozenHeadPaddingTop
                ? `${this.frozenHeadPaddingTop}px`
                : '';
            theadEl.style.clipPath = `inset(0 ${rightClip}px 0 ${leftClip}px)`;
            theadEl.style.transform = `translate3d(${-scrollLeft}px, 0, 0)`;
            theadEl.style.zIndex = '1002';
            theadEl.style.pointerEvents = 'auto';
            theadEl.style.transition = isEnteringFrozenMode ? 'top 120ms ease' : '';
            theadEl.querySelectorAll('th').forEach((thEl) => {
                thEl.style.pointerEvents = 'auto';
            });
            this._frozenHeadThEls.forEach((thEl) => {
                thEl.style.transform = `translate3d(${scrollLeft}px, 0, 0)`;
            });

            // Measure after applying fixed styles so body offset always matches actual frozen header height.
            const resolvedHeadHeight = Math.ceil(theadEl.getBoundingClientRect().height || initialHeadHeight);
            this.frozenHeadHeight = resolvedHeadHeight;
            tableEl.style.marginTop = '0';
            wrapperEl.style.transition = isEnteringFrozenMode ? 'padding-top 120ms ease' : '';
            wrapperEl.style.paddingTop = `${resolvedHeadHeight}px`;
        },
    },
    components: {
        DataTable,
        Paginator,
    }
}
</script>

<style scoped>
.adapters_data_table_root {
    width: 100%;
}

.adapters_data_table__frozen_head :deep(.p-datatable .p-datatable-wrapper) {
    overflow-x: auto !important;
    overflow-y: visible !important;
}

.adapters_data_table__frozen_head :deep(.p-datatable .p-datatable-thead > tr > th) {
    background: var(--surface-card);
    pointer-events: auto;
}
</style>

<!-- Teleport в body: scoped-стили на него не действуют -->
<style>
.adapters_data_table__fixed_h_scroll {
    position: fixed;
    bottom: 0;
    z-index: 1001;
    overflow-x: auto;
    overflow-y: hidden;
    height: 14px;
    background: var(--surface-card, #fff);
    box-shadow: 0 -1px 0 var(--surface-border, rgba(0, 0, 0, 0.08));
}

.adapters_data_table__fixed_h_scroll_content {
    height: 1px;
    pointer-events: none;
}
</style>
